import api from 'api';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { ClipType, InfluencerComparisonType, InfluencerDiscoveryType, RootState } from 'types';
import FiltersMenu from '../filters-menu';
import './influencer-comparison.scss';
import InfluencerModal from './influencer-modal';
import ReactECharts from 'echarts-for-react';
import { actions } from 'redux/reducers';
import ClipTray from 'components/app/clip-tray';
import { useQuery } from  'hooks/query';

function InfluencerComparison() {
    const params = useParams<any>();
    const dispatch = useDispatch();
    const history = useHistory();
    const query = useQuery();
    const project = useSelector((state: RootState) => state.project);
    const filtersMenuRef = useRef<any>();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState<Moment>(moment(query.get('datefrom')).set('h', 0).set('m', 0).set('s', 0));
    const [dateTo, setDateTo] = useState<Moment>(moment(query.get('dateto')));
    const [influencerIds, setInfluencerIds] = useState<number[]>([]);
    const [streamIds, setStreamIds] = useState<number[]>([]);
    const [influencers, setInfluencers] = useState<InfluencerDiscoveryType[]>([]);
    const [influencerModalOpen, setInfluencerModalOpen] = useState(false);
    const [influencersToCompare, setInfluencersToCompare] = useState<InfluencerComparisonType[]>([]);
    const [legend, setLegend] = useState<string[]>([]);
    const [xAxis, setXAxis] = useState<string[]>([]);
    const [chartData, setChartData] = useState<number[]>([]);
    // const [pieChartData, setPieChartData] = useState<object[]>([]);
    const [streamidList]=useState<number[]>(query.get('streams')!.split(',').map(x => parseInt(x)));

    const getInfluencers = useCallback(async () => {
        const data = await api.influencerDiscovery.getAll(params.projectId);
        setInfluencers(data.filter((x : InfluencerDiscoveryType) => streamIds.includes(x.StreamId)));
        console.log(data);
        if(query.get('ids')){
            setInfluencerIds(query.get('ids')!.split(',').map(x => parseInt(x)));
        }else{
            if(data.length>2){
                const ids=data.slice(0,2).map((x:any)=>x.Id);
                query.set('ids', ids.join(','));
                setInfluencerIds(ids);

                history.replace({
                    search: `?${query.toString()}`
                })
            }      
        }
    }, [history, params.projectId, query, streamIds]);

    

    const getInfluencersToCompare = useCallback(async () => {
       
        if (streamIds.length && influencerIds.length) {
        
            const data: InfluencerComparisonType[] = await api.influencerComaprison.getAll(influencerIds.join(','), dateFrom, dateTo, streamIds.join(','), params.projectId);
            
            setLegend(data.map(x => `${x.Profile.AuthorName}`));

            let _xAxis: string[] = [];
            const days = dateTo.diff(dateFrom, 'days');
            let arr: any[] = [];
    
            if (days >= 30 && days < 90) {
                for (let i = dateFrom.clone(); i.isSameOrBefore(dateTo); i.add(7, 'days')) {
                    let dtFrom = i;
                    let dtTo = i.clone().add(6, 'd');
                    if (dtTo > moment()) {
                        dtTo = moment();
                    }
                    _xAxis.push(`${dtFrom.format('D MMM YYYY')} - ${dtTo.format('D MMM YYYY')}`);
                    setXAxis(_xAxis);
                }
                for (let i = 0; i < influencerIds.length; i++) {
                    arr.push([]);
                    for (let j = dateFrom.clone(); j.isSameOrBefore(dateTo); j.add(7, 'days')) {
                        let dtFrom = j;
                        let dtTo = j.clone().add(6, 'd');
                        if (dtTo > moment()) {
                            dtTo = moment();
                        }
                        if (data[i]) {
                            const c = data[i].Clips.filter(x => moment(x.DatePosted).isBetween(dtFrom, dtTo));
                            arr[i].push(c.length)
                            setChartData(arr);
                        }
                    }
                }
            } else if (days >= 90) {
                for (let i = dateFrom.clone(); i.isSameOrBefore(dateTo); i.add(1, 'month')) {
                    let dtFrom = i;
                    let dtTo = i.clone().add(1, 'M').subtract(1, 'd');
                    if (dtTo > moment()) {
                        dtTo = moment();
                    }
                    _xAxis.push(`${dtFrom.format('D MMM YYYY')} - ${dtTo.format('D MMM YYYY')}`);
                    setXAxis(_xAxis);
                }
                for (let i = 0; i < influencerIds.length; i++) {
                    arr.push([]);
                    for (let j = dateFrom.clone(); j.isSameOrBefore(dateTo); j.add(1, 'month')) {
                        let dtFrom = j;
                        let dtTo = j.clone().add(1, 'M').subtract(1, 'd');
                        if (dtTo > moment()) {
                            dtTo = moment();
                        }
                        if (data[i]) {
                            const c = data[i].Clips.filter(x => moment(x.DatePosted).isBetween(dtFrom, dtTo));
                            arr[i].push(c.length)
                            setChartData(arr);
                        }
                    }
                }
            } else {
                for (let i = dateFrom.clone(); i.isSameOrBefore(dateTo); i.add(1, 'day')) {
                    _xAxis.push(`${i.format('D MMM YYYY')}`);
                    setXAxis(_xAxis);
                }
                
                for (let i = 0; i < influencerIds.length; i++) {
                    arr.push([]);
                    for (let j = dateFrom.clone(); j.isSameOrBefore(dateTo); j.add(1, 'day')) {
                        if (data[i]) {
                            const c = data[i].Clips.filter(x => moment(x.DatePosted).format('D MMM YYYY') === j.format('D MMM YYYY'));
                            arr[i].push(c.length)
                            setChartData(arr);
                        }
                    }
                }
            }

            setInfluencersToCompare(data);
        }
    }, [params.projectId, dateFrom, dateTo, streamIds, influencerIds]);

    const removeInfluencer = (id: number) => {
        if (influencerIds.includes(id)) {
            setInfluencerIds(influencerIds.filter(x => x !== id));
        } else {
            setInfluencerIds([
                ...influencerIds,
                id
            ])
        }
        
    }

    const onClick = useCallback(async (e: any) => {
        e.event.stop();
        dispatch(actions.clipTray.set({
            loading: true,
            clips: [],
            open: true
        }))
        const dtFrom = e.name.split(' - ')[0];
        const dtTo = e.name.split(' - ')[1];
        const clips: ClipType[] = await api.clip.getAll(params.projectId, moment(dtFrom), dtTo ? moment(dtTo) : moment(dtFrom).add(1, 'd'), streamIds.join(','));
        dispatch(actions.clipTray.set({
            loading: false,
            clips: clips.filter(x => x.AuthorName = e.seriesName),
            open: true
        }))
    }, [dispatch, params.projectId, streamIds])

    const onPieClick = useCallback(async (e: any) => {
        e.event.stop();
        dispatch(actions.clipTray.set({
            loading: true,
            clips: [],
            open: true
        }))
        let clips: ClipType[] = await api.clip.getAll(params.projectId, dateFrom, dateTo, streamIds.join(','));
        dispatch(actions.clipTray.set({
            loading: false,
            clips: clips.filter(x => x.AuthorName = e.name),
            open: true
        }))
    }, [dispatch, params.projectId, streamIds, dateFrom, dateTo])

    useEffect(() => {
        if (project) {
            setStreamIds([7, 8, 9]);
            if (project.ProjectTypeId === 5) {
                setStreamIds([7, 8, 9]);
            } else {
                if (project.ProjectSubTypeId === 7) {
                    setStreamIds([7]);
                } else if (project.ProjectSubTypeId === 8) {
                    setStreamIds([8]);
                } else if (project.ProjectSubTypeId === 9) {
                    setStreamIds([10]);
                } else if (project.ProjectSubTypeId === 10) {
                    setStreamIds([9]);
                } else if (project.ProjectSubTypeId === 11) {
                    setStreamIds([11]);
                }
            }
        }
    }, [project])

    useEffect(() =>{
        getInfluencersToCompare();
    }, [getInfluencersToCompare])

    useEffect(() => {
        getInfluencers();
    }, [getInfluencers])
    
    return (
        <div className="influencer-comparison">
            <div className="top">
                <i className="fal fa-arrow-circle-left" onClick={() => history.goBack()}></i>
                <p className="project-name">{project?.Description}</p>
                <p className="project-type">{project?.ProjectType}</p>
                <span className='filters' ref={filtersMenuRef} onClick={() => setFiltersOpen(!filtersOpen)}>Filter<i className={`fal ${filtersOpen ? 'fa-times' : 'fa-sliders-h'}`}></i></span>
                <FiltersMenu 
                    open={filtersOpen}
                    setOpen={setFiltersOpen}
                    dateFrom={dateFrom}
                    setDateFrom={setDateFrom}
                    dateTo={dateTo}
                    setDateTo={setDateTo}
                    streamIds={streamIds}
                    setStreamIds={setStreamIds}
                    projectTypeId={project?.ProjectTypeId}
                    showDatePicker={true}
                    buttonRef={filtersMenuRef}
                />
            </div>
            <div className="body">
                <button className="btn-compare-influencers" onClick={() => setInfluencerModalOpen(true)}>Compare Influencers</button>
                <div className="influencers">
                    {influencersToCompare.map((x, i) => (
                        <div className="influencer" key={i}>
                            <span onClick={() => removeInfluencer(x.Profile.Id)}>x</span>
                            <i className={`fas fa-user-circle`}></i>
                            <p>{x.Profile.AuthorName}</p>
                        </div>
                    ))}
                </div>
                <div className="chart">
                    <ReactECharts
                        notMerge={true}
                        option={{
                            tooltip: {},
                            legend: {
                                data: legend,
                                top: 10
                            },
                            grid: {
                                left: 30,
                                right: 10,
                                top: 45,
                                bottom: 20
                            },
                            xAxis: {
                                data: xAxis
                            },
                            yAxis: {
                                minInterval: 1
                            },
                            series: influencersToCompare.map((x, i) => {
                                return {
                                    name: `${x.Profile.AuthorName}`,
                                    type: 'bar',
                                    data: chartData[i]
                                }
                            })
                        }}
                        onEvents={{
                            'click': onClick
                        }}
                    />
                </div>
                <div className="chart">
                    <ReactECharts
                        option={{
                            tooltip: {
                                trigger: 'item',
                                formatter: '{b}<br/>{c} ({d}%)'
                            },
                            legend: {
                                data: legend
                            },
                            series: {
                                type: 'pie',
                                data: influencersToCompare.map(x => {
                                    return {
                                        name: `${x.Profile.AuthorName}`,
                                        value: x.Clips.length
                                    }
                                }),
                                radius: ['50%', '75%'],
                                label: {
                                    show: false,
                                    position: 'center'
                                },
                                emphasis: {
                                    label: {
                                        show: true,
                                        fontWeight: 'bold'
                                    }
                                },
                                labelLine: {
                                    show: false
                                },
                            }
                        }
                        }
                        notMerge={true}
                        lazyUpdate={true}
                        onEvents={{
                            'click': onPieClick
                        }}
                    />
                </div>
            </div>
            <ClipTray />
            <InfluencerModal 
                open={influencerModalOpen}
                setOpen={setInfluencerModalOpen}
                influencers={influencers}
                influencerIds={influencerIds}
                setInfluencerIds={setInfluencerIds} streamIdList={streamidList}            />
        </div>
    )
}

export default InfluencerComparison;